/**
 * Penanda Peran (Role Marker) — pemetaan profesi/role → spesifikasi visual
 * (warna, ikon, bentuk, alt-text) untuk menandai **fitur ini untuk siapa**.
 *
 * Sumber kebenaran kepemilikan fitur = matriks RBAC yang sama dengan kontrol
 * akses (`src/data/rbac/form-access.data.ts`). Marker TIDAK mendefinisikan
 * aturan baru — ia hanya *memvisualkan* siapa pemilik/pengguna sah suatu form
 * sesuai regulasi (lihat audit `docs/AUDIT_ROLE_ACCESS_REGULASI.md`).
 *
 * Aksesibilitas: setiap penanda WAJIB membawa `altText` (dipakai sebagai
 * `aria-label`/`title`) sehingga tidak bergantung pada warna/bentuk semata.
 */

import { AccessRule, FormAction } from "@/utils/form-access";

export type RoleKey =
  | "dokter"
  | "forensik"
  | "bidan"
  | "perawat"
  | "psikolog"
  | "farmasi"
  | "gizi"
  | "lab"
  | "radiologi"
  | "rekam_medis"
  | "admisi"
  | "kasir"
  | "manajemen"
  | "tim_ppi"
  | "tim_kprs";

/** Bentuk visual penanda — tidak menjadi satu-satunya pembeda (selalu + ikon + alt-text). */
export type RoleShape = "pill" | "tag";

export interface RoleSpec {
  key: RoleKey;
  /** Label panjang (tooltip / aria). */
  label: string;
  /** Label ringkas pada chip. */
  short: string;
  /** Warna latar (hex) — dipakai untuk filled & sebagai warna garis outline. */
  color: string;
  /** Ikon Material Design Icons. */
  icon: string;
  /** Bentuk dasar penanda. `pill` (kapsul) utk profesi medis penanda-tangan; `tag` utk profesi pengisi. */
  shape: RoleShape;
  /** Teks alternatif lengkap (aria-label/title) — penjelasan regulatif singkat. */
  altText: string;
}

/**
 * Spesifikasi visual per role. Warna & ikon dipilih konsisten lintas modul
 * agar pengguna belajar sekali, kenal di mana saja.
 */
export const ROLE_SPECS: Record<RoleKey, RoleSpec> = {
  dokter: {
    key: "dokter",
    label: "Dokter / DPJP",
    short: "Dokter",
    color: "#1565C0", // blue darken-3
    icon: "mdi-stethoscope",
    shape: "pill",
    altText:
      "Fitur untuk Dokter (DPJP)",
  },
  forensik: {
    key: "forensik",
    label: "Dokter Forensik & Medikolegal",
    short: "Forensik",
    color: "#B71C1C", // red darken-4
    icon: "mdi-gavel",
    shape: "pill",
    altText:
      "Fitur untuk Dokter Spesialis Forensik & Medikolegal",
  },
  bidan: {
    key: "bidan",
    label: "Bidan",
    short: "Bidan",
    color: "#AD1457", // pink darken-3
    icon: "mdi-human-pregnant",
    shape: "tag",
    altText:
      "Fitur untuk Bidan",
  },
  perawat: {
    key: "perawat",
    label: "Perawat",
    short: "Perawat",
    color: "#2E7D32", // green darken-3
    icon: "mdi-medical-bag",
    shape: "tag",
    altText:
      "Fitur untuk Perawat",
  },
  psikolog: {
    key: "psikolog",
    label: "Psikolog Klinis",
    short: "Psikolog",
    color: "#00838F", // cyan darken-3
    icon: "mdi-brain",
    shape: "tag",
    altText:
      "Fitur untuk Psikolog Klinis",
  },
  farmasi: {
    key: "farmasi",
    label: "Apoteker / Tenaga Teknis Kefarmasian",
    short: "Farmasi",
    color: "#6A1B9A", // purple darken-2
    icon: "mdi-pill",
    shape: "tag",
    altText:
      "Fitur untuk Apoteker/TTK",
  },
  gizi: {
    key: "gizi",
    label: "Nutrisionis / Dietisien",
    short: "Gizi",
    color: "#EF6C00", // orange darken-3
    icon: "mdi-food-apple",
    shape: "tag",
    altText:
      "Fitur untuk Nutrisionis/Dietisien",
  },
  lab: {
    key: "lab",
    label: "Laboratorium",
    short: "Lab",
    color: "#00695C", // teal darken-2
    icon: "mdi-flask",
    shape: "tag",
    altText:
      "Fitur untuk Analis/Pranata Laboratorium Medik (ATLM)",
  },
  radiologi: {
    key: "radiologi",
    label: "Radiologi",
    short: "Radiologi",
    color: "#283593", // indigo darken-2
    icon: "mdi-radiology-box",
    shape: "tag",
    altText:
      "Fitur untuk Radiografer/Radiologi",
  },
  rekam_medis: {
    key: "rekam_medis",
    label: "Rekam Medis (PMIK)",
    short: "Rekam Medis",
    color: "#4E342E", // brown darken-2
    icon: "mdi-folder-account",
    shape: "tag",
    altText:
      "Fitur untuk Perekam Medis & Informasi Kesehatan (PMIK)",
  },
  admisi: {
    key: "admisi",
    label: "Admisi / Pendaftaran",
    short: "Admisi",
    color: "#455A64", // blue-grey darken-1
    icon: "mdi-clipboard-account",
    shape: "tag",
    altText: "Fitur untuk Petugas Admisi/Pendaftaran pasien.",
  },
  kasir: {
    key: "kasir",
    label: "Kasir / Billing",
    short: "Kasir",
    color: "#37474F", // blue-grey darken-2
    icon: "mdi-cash-register",
    shape: "tag",
    altText: "Fitur untuk Kasir/Billing.",
  },
  manajemen: {
    key: "manajemen",
    label: "Manajemen / Direksi",
    short: "Manajemen",
    color: "#546E7A", // blue-grey
    icon: "mdi-chart-box",
    shape: "tag",
    altText:
      "Fitur untuk Manajemen/Direksi",
  },
  tim_ppi: {
    key: "tim_ppi",
    label: "Tim PPI",
    short: "PPI",
    color: "#0097A7", // cyan darken-1
    icon: "mdi-shield-virus",
    shape: "tag",
    altText:
      "Fitur untuk Tim PPI (Pencegahan & Pengendalian Infeksi)",
  },
  tim_kprs: {
    key: "tim_kprs",
    label: "Tim Mutu / Keselamatan Pasien (KPRS)",
    short: "Mutu/KPRS",
    color: "#5D4037", // brown
    icon: "mdi-shield-check",
    shape: "tag",
    altText:
      "Fitur untuk Tim Mutu/Keselamatan Pasien (KPRS)",
  },
};

/** Profesi (string dari `User.profesi` / aturan RBAC) → RoleKey. */
function profesiToKey(profesi: string): RoleKey | null {
  switch (profesi.trim()) {
    case "Dokter":
      return "dokter";
    case "Perawat":
      return "perawat";
    case "Bidan":
      return "bidan";
    case "Psikolog":
      return "psikolog";
    case "Farmasi":
      return "farmasi";
    case "Gizi":
      return "gizi";
    default:
      return null;
  }
}

/** unit_kategori (untuk aturan profesi kosong) → RoleKey bila relevan ditandai. */
function unitKategoriToKey(unitKategori: string): RoleKey | null {
  switch (unitKategori) {
    case "forensik":
      return "forensik";
    case "lab":
      return "lab";
    case "radiologi":
      return "radiologi";
    case "rekam_medis":
      return "rekam_medis";
    case "admisi":
      return "admisi";
    case "kasir":
      return "kasir";
    case "manajemen":
      return "manajemen";
    case "tim_ppi":
      return "tim_ppi";
    case "tim_kprs":
      return "tim_kprs";
    case "gizi":
      return "gizi";
    default:
      return null;
  }
}

export interface RoleOwner extends RoleSpec {
  /** Role ini boleh TTD (penanggung jawab / penanda-tangan dokumen). */
  canSign: boolean;
  /** Role ini punya akses laporan (agregat). */
  canReport: boolean;
  /** Pemilik utama fitur (penanda-tangan, atau pengisi utama bila tak ada TTD). */
  primary: boolean;
}

// Urutan tampil: profesi medis penanda-tangan dulu, lalu kebidanan/keperawatan,
// lalu profesi penunjang, lalu peran administratif/manajerial.
const DISPLAY_ORDER: RoleKey[] = [
  "dokter",
  "forensik",
  "bidan",
  "perawat",
  "psikolog",
  "farmasi",
  "gizi",
  "lab",
  "radiologi",
  "rekam_medis",
  "tim_ppi",
  "tim_kprs",
  "admisi",
  "kasir",
  "manajemen",
];

let _rulesCache: Record<string, AccessRule[]> | null = null;
function getRules(): Record<string, AccessRule[]> {
  if (_rulesCache) return _rulesCache;
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const mod = require("@/data/rbac/form-access.data");
  _rulesCache = mod.FORM_ACCESS_RULES;
  return _rulesCache as Record<string, AccessRule[]>;
}

/** Override untuk testing. `null` reset ke generated. */
export function __setRoleMarkerRulesForTesting(
  rules: Record<string, AccessRule[]> | null
): void {
  _rulesCache = rules;
}

/**
 * Daftar role pemilik/pengguna sah suatu form (diturunkan dari matriks RBAC).
 * Hasil ter-dedupe & terurut sesuai `DISPLAY_ORDER`, dengan flag `primary`
 * (penanda-tangan; jika tak ada, pengisi klinis pertama).
 */
export function formRoleOwners(formCode: string): RoleOwner[] {
  const rules = getRules()[formCode];
  if (!rules || rules.length === 0) return [];

  const acc = new Map<
    RoleKey,
    { canSign: boolean; canReport: boolean }
  >();

  for (const r of rules) {
    const key =
      r.profesi && r.profesi.trim() !== ""
        ? profesiToKey(r.profesi)
        : unitKategoriToKey(r.unitKategori);
    if (!key) continue;
    const prev = acc.get(key) || { canSign: false, canReport: false };
    const actions = r.actions as FormAction[];
    acc.set(key, {
      canSign: prev.canSign || actions.includes("sign"),
      canReport: prev.canReport || actions.includes("report"),
    });
  }

  const owners: RoleOwner[] = [];
  for (const key of DISPLAY_ORDER) {
    const hit = acc.get(key);
    if (!hit) continue;
    owners.push({
      ...ROLE_SPECS[key],
      canSign: hit.canSign,
      canReport: hit.canReport,
      primary: false,
    });
  }

  // Tentukan primary: role yang bisa TTD; bila tak ada, pengisi klinis pertama.
  const signers = owners.filter((o) => o.canSign);
  if (signers.length > 0) {
    signers.forEach((o) => (o.primary = true));
  } else if (owners.length > 0) {
    owners[0].primary = true;
  }

  return owners;
}
